import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowRight, ChevronDown, ChevronRight, User, ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, NAV_MORE, PHONE } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [mobileTab, setMobileTab] = useState(null);
  const { user, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const resolveHref = (href) => (href.startsWith("#") && !isHome ? `/${href}` : href);

  const isSubItemActive = (href) => {
    if (!href || href.startsWith("#")) return false;
    const clean = href.split("#")[0];
    return pathname === clean || pathname.startsWith(`${clean}/`);
  };

  const isLinkActive = (link) => {
    if (!link) return false;
    if (link.href && !link.href.startsWith("#")) {
      if (link.href === "/" && pathname === "/") return true;
      if (link.href !== "/" && (pathname === link.href || pathname.startsWith(`${link.href}/`))) {
        return true;
      }
    }
    if (link.mega?.tabs) {
      for (const tab of link.mega.tabs) {
        if (tab.href && !tab.href.startsWith("#")) {
          const tabBase = tab.href.split("#")[0];
          if (tabBase && (pathname === tabBase || pathname.startsWith(`${tabBase}/`))) {
            return true;
          }
        }
        for (const item of tab.items || []) {
          const itemBase = item.href.split("#")[0];
          if (itemBase && (pathname === itemBase || pathname.startsWith(`${itemBase}/`))) {
            return true;
          }
        }
      }
    }
    if (link.dropdown) {
      for (const item of link.dropdown) {
        const itemBase = item.href.split("#")[0];
        if (itemBase && (pathname === itemBase || pathname.startsWith(`${itemBase}/`))) {
          return true;
        }
      }
    }
    return false;
  };

  const isMoreActive = () => {
    if (
      NAV_MORE.some((item) => {
        const base = item.href.split("#")[0];
        return pathname === base || pathname.startsWith(`${base}/`);
      })
    ) {
      return true;
    }
    const policyRoutes = [
      "/about",
      "/pricing",
      "/faq",
      "/blogs",
      "/terms",
      "/privacy-policy",
      "/data-security-policy",
      "/data-retention-policy",
      "/refund-policy",
      "/cookie-policy",
      "/grievance-redressal-policy",
    ];
    return policyRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  };

  const isContactActive = pathname === "/contact" || pathname.startsWith("/contact");

  const navLinkClass = (active) =>
    `relative cursor-pointer whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors duration-200 after:absolute after:inset-x-4 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-gold-500 after:transition-transform after:duration-300 hover:text-gold-600 hover:after:scale-x-100 ${
      active ? "text-gold-600 after:scale-x-100" : "text-navy-800 after:scale-x-0"
    }`;

  const navBtnClass = (active, isOpen = false) =>
    `relative flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors duration-200 after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-gold-500 after:transition-transform after:duration-300 hover:text-gold-600 hover:after:scale-x-100 ${
      active || isOpen ? "text-gold-600 after:scale-x-100" : "text-navy-800 after:scale-x-0"
    }`;

  const NavAnchor = ({ href, className, onClick, children, ...rest }) => {
    if (href.startsWith("#") && isHome) {
      return (
        <a href={href} className={className} onClick={onClick} {...rest}>
          {children}
        </a>
      );
    }
    return (
      <Link to={resolveHref(href)} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50"
        data-testid="navbar"
      >
        <div
          className={`bg-[rgb(238,242,250)]/90 backdrop-blur-xl border-b border-navy-900/10 transition-shadow duration-300 ${
            scrolled ? "shadow-[0_10px_30px_rgba(15,30,60,0.08)]" : "shadow-[0_1px_0_rgba(15,30,60,0.04)]"
          }`}
        >
          <nav className="mx-auto flex max-w-[96rem] items-center justify-between px-5 py-3 lg:px-8">
            <Logo variant="dark" />

            <div className="flex items-center gap-2">
            <ul className="hidden items-center gap-1 xl:flex">
              {NAV_LINKS.map((link) =>
                link.mega ? (
                  <li
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => {
                      setOpenDropdown(link.label);
                      setActiveTab(0);
                    }}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenDropdown((v) => (v === link.label ? null : link.label))}
                      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={navBtnClass(isLinkActive(link), openDropdown === link.label)}
                    >
                      {link.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === link.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {openDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className={link.mega.tabs.length > 1 ? "absolute left-0 top-full w-[46rem] pt-2" : "absolute left-0 top-full w-[26rem] pt-2"}
                          data-testid={`nav-mega-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {link.mega.tabs.length > 1 ? (
                            <div className="grid grid-cols-[13rem_1fr] overflow-hidden rounded-2xl border border-navy-900/10 bg-white shadow-[0_20px_44px_rgba(15,30,60,0.14)]">
                              {/* Tabs */}
                              <div className="space-y-1 border-r border-navy-900/10 bg-[rgb(238,242,250)]/60 p-3">
                                {link.mega.tabs.map((tab, i) => (
                                  <button
                                    key={tab.label}
                                    type="button"
                                    onMouseEnter={() => setActiveTab(i)}
                                    data-testid={`nav-mega-tab-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold transition-colors ${
                                      activeTab === i ? "bg-white text-gold-600 shadow-[0_2px_8px_rgba(15,30,60,0.06)]" : "text-navy-800 hover:bg-white/70"
                                    }`}
                                  >
                                    {tab.label}
                                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-opacity ${activeTab === i ? "opacity-100" : "opacity-0"}`} />
                                  </button>
                                ))}
                              </div>
                              {/* Active tab's services */}
                              <div className="p-4">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                  {link.mega.tabs[activeTab].items.map((item) => (
                                    <Link
                                      key={item.label}
                                      to={item.href}
                                      onClick={() => setOpenDropdown(null)}
                                      data-testid={`nav-mega-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                      className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                        isSubItemActive(item.href) ? "bg-navy-50/70 text-gold-600 font-semibold" : "text-navy-800"
                                      }`}
                                    >
                                      {item.label}
                                    </Link>
                                  ))}
                                </div>
                                <Link
                                  to={link.mega.hubHref}
                                  onClick={() => setOpenDropdown(null)}
                                  className="mt-2 flex items-center gap-1.5 border-t border-navy-900/10 px-3 pt-3 text-sm font-semibold text-navy-900 transition-colors hover:text-gold-600"
                                >
                                  {link.mega.hubLabel} <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                              </div>
                            </div>
                          ) : (
                            // Single-category mega menus (Other Services) skip the tab sidebar
                            // entirely — with one tab it just duplicated the parent's own label
                            // ("Other Services" nested under "Other Services").
                            <div className="overflow-hidden rounded-2xl border border-navy-900/10 bg-white p-4 shadow-[0_20px_44px_rgba(15,30,60,0.14)]">
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {link.mega.tabs[0].items.map((item) => (
                                  <Link
                                    key={item.label}
                                    to={item.href}
                                    onClick={() => setOpenDropdown(null)}
                                    data-testid={`nav-mega-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                      isSubItemActive(item.href) ? "bg-navy-50/70 text-gold-600 font-semibold" : "text-navy-800"
                                    }`}
                                  >
                                    {item.label}
                                  </Link>
                                ))}
                              </div>
                              <Link
                                to={link.mega.hubHref}
                                onClick={() => setOpenDropdown(null)}
                                className="mt-2 flex items-center gap-1.5 border-t border-navy-900/10 px-3 pt-3 text-sm font-semibold text-navy-900 transition-colors hover:text-gold-600"
                              >
                                {link.mega.hubLabel} <ArrowRight className="h-3.5 w-3.5" />
                              </Link>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                ) : link.dropdown ? (
                  <li
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenDropdown((v) => (v === link.label ? null : link.label))}
                      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={navBtnClass(isLinkActive(link), openDropdown === link.label)}
                    >
                      {link.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === link.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {openDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className="absolute left-0 top-full pt-2"
                          data-testid={`nav-dropdown-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <div className="min-w-[15rem] overflow-hidden rounded-2xl border border-navy-900/10 bg-white py-2 shadow-[0_16px_36px_rgba(15,30,60,0.12)]">
                            {link.dropdown.map((item) => (
                              <Link
                                key={item.label}
                                to={item.href}
                                onClick={() => setOpenDropdown(null)}
                                data-testid={`nav-dropdown-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`block px-4 py-2.5 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                  isSubItemActive(item.href) ? "bg-navy-50/70 text-gold-600 font-semibold" : "text-navy-800"
                                }`}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                ) : (
                  <li key={link.label}>
                    <NavAnchor
                      href={link.href}
                      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={navLinkClass(isLinkActive(link))}
                    >
                      {link.label}
                    </NavAnchor>
                  </li>
                )
              )}
              <li
                className="relative"
                onMouseEnter={() => setMoreOpen(true)}
                onMouseLeave={() => setMoreOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  data-testid="nav-link-more"
                  className={navBtnClass(isMoreActive(), moreOpen)}
                >
                  More
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {moreOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 top-full pt-2"
                      data-testid="nav-more-dropdown"
                    >
                      <div className="min-w-[10rem] overflow-hidden rounded-2xl border border-navy-900/10 bg-white py-2 shadow-[0_16px_36px_rgba(15,30,60,0.12)]">
                        {NAV_MORE.map((item) => (
                          <Link
                            key={item.label}
                            to={item.href}
                            onClick={() => setMoreOpen(false)}
                            data-testid={`nav-more-${item.label.toLowerCase()}`}
                            className={`block px-4 py-2.5 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                              isSubItemActive(item.href) ? "bg-navy-50/70 text-gold-600 font-semibold" : "text-navy-800"
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
              <li>
                <NavAnchor href="/contact" data-testid="nav-link-contact" className={navLinkClass(isContactActive)}>
                  Contact
                </NavAnchor>
              </li>
            </ul>

            <div className="hidden items-center gap-6 xl:flex ml-3">
              {/* <a
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                data-testid="nav-phone"
                className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 transition-colors hover:text-gold-600"
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-navy-900/5">
                  <Phone className="h-4 w-4 text-emerald2-500 animate-ping" />
                </span>
                {PHONE}
              </a> */}
              <Link
                to={user ? (isAdmin ? "/admin" : "/account") : "/login"}
                data-testid={user ? "nav-account-link" : "nav-login-link"}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold transition-colors hover:text-gold-600 ${
                  pathname === "/login" || pathname === "/account" || pathname === "/admin" ? "text-gold-600" : "text-navy-800"
                }`}
              >
                {isAdmin ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                {user ? (isAdmin ? "Admin" : user.name.split(" ")[0]) : "Log In"}
              </Link>
              <Link
                to="/contact"
                data-testid="nav-book-consultation"
                className="group inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gold-500 px-3.5 py-2.5 text-sm font-semibold text-navy-950 shadow-[0_8px_22px_rgba(212,162,76,0.4)] transition-transform duration-300 hover:scale-105"
              >
                Book Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </div>

            <button
              type="button"
              aria-label="Open menu"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              className="grid h-11 w-11 place-items-center rounded-full border border-navy-900/10 bg-[rgb(238,242,250)]/70 text-navy-900 backdrop-blur xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            </div>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[rgb(238,242,250)] p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <Logo variant="dark" />
                <button
                  type="button"
                  aria-label="Close menu"
                  data-testid="nav-mobile-close"
                  onClick={() => setOpen(false)}
                  className="grid h-10 w-10 place-items-center rounded-full border border-navy-900/10 text-navy-900"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ul className="mt-8 space-y-1">
                {NAV_LINKS.map((link) =>
                  link.mega ? (
                    <li key={link.label}>
                      <button
                        type="button"
                        onClick={() => setMobileDropdown((v) => (v === link.label ? null : link.label))}
                        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-lg font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                          isLinkActive(link) ? "bg-navy-50 text-gold-600 font-semibold" : "text-navy-800"
                        }`}
                      >
                        {link.label}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileDropdown === link.label ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileDropdown === link.label && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden pl-4"
                          >
                            {link.mega.tabs.length === 1
                              ? // Single-category mega menus skip the redundant tab level — it just
                                // repeated the parent's own label (e.g. "Other Services" under
                                // "Other Services") and cost users an extra tap for nothing.
                                link.mega.tabs[0].items.map((item) => (
                                  <li key={item.label}>
                                    <Link
                                      to={item.href}
                                      onClick={() => setOpen(false)}
                                      className={`block rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                        isSubItemActive(item.href) ? "text-gold-600 font-semibold" : "text-navy-600"
                                      }`}
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))
                              : link.mega.tabs.map((tab) => {
                                  const tabKey = `${link.label}::${tab.label}`;
                                  return (
                                    <li key={tab.label}>
                                      <button
                                        type="button"
                                        onClick={() => setMobileTab((v) => (v === tabKey ? null : tabKey))}
                                        data-testid={`nav-mobile-tab-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={`flex w-full items-center justify-between rounded-xl px-4 py-2.5 text-base font-semibold transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                          mobileTab === tabKey ? "text-gold-600" : "text-navy-700"
                                        }`}
                                      >
                                        {tab.label}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${mobileTab === tabKey ? "rotate-180" : ""}`} />
                                      </button>
                                      <AnimatePresence>
                                        {mobileTab === tabKey && (
                                          <motion.ul
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden pl-4"
                                          >
                                            {tab.items.map((item) => (
                                              <li key={item.label}>
                                                <Link
                                                  to={item.href}
                                                  onClick={() => setOpen(false)}
                                                  className={`block rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                                    isSubItemActive(item.href) ? "text-gold-600 font-semibold" : "text-navy-600"
                                                  }`}
                                                >
                                                  {item.label}
                                                </Link>
                                              </li>
                                            ))}
                                          </motion.ul>
                                        )}
                                      </AnimatePresence>
                                    </li>
                                  );
                                })}
                            <li>
                              <Link
                                to={link.mega.hubHref}
                                onClick={() => setOpen(false)}
                                className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-navy-900 hover:bg-navy-50 hover:text-gold-600"
                              >
                                {link.mega.hubLabel}
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </li>
                  ) : link.dropdown ? (
                    <li key={link.label}>
                      <button
                        type="button"
                        onClick={() => setMobileDropdown((v) => (v === link.label ? null : link.label))}
                        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-lg font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                          isLinkActive(link) ? "bg-navy-50 text-gold-600 font-semibold" : "text-navy-800"
                        }`}
                      >
                        {link.label}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileDropdown === link.label ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileDropdown === link.label && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden pl-4"
                          >
                            {link.dropdown.map((item) => (
                              <li key={item.label}>
                                <Link
                                  to={item.href}
                                  onClick={() => setOpen(false)}
                                  className={`block rounded-xl px-4 py-2.5 text-base font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                                    isSubItemActive(item.href) ? "text-gold-600 font-semibold" : "text-navy-700"
                                  }`}
                                >
                                  {item.label}
                                </Link>
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <NavAnchor
                        href={link.href}
                        onClick={() => setOpen(false)}
                        className={`block cursor-pointer rounded-xl px-4 py-3 text-lg font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                          isLinkActive(link) ? "bg-navy-50 text-gold-600 font-semibold" : "text-navy-800"
                        }`}
                      >
                        {link.label}
                      </NavAnchor>
                    </li>
                  )
                )}
                <li className="px-4 pt-3 text-xs font-bold uppercase tracking-wider text-navy-400">More</li>
                {NAV_MORE.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className={`block cursor-pointer rounded-xl px-4 py-3 text-lg font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                        isSubItemActive(link.href) ? "bg-navy-50 text-gold-600 font-semibold" : "text-navy-800"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <NavAnchor
                    href="/contact"
                    onClick={() => setOpen(false)}
                    className={`block cursor-pointer rounded-xl px-4 py-3 text-lg font-medium transition-colors hover:bg-navy-50 hover:text-gold-600 ${
                      isContactActive ? "bg-navy-50 text-gold-600 font-semibold" : "text-navy-800"
                    }`}
                  >
                    Contact
                  </NavAnchor>
                </li>
              </ul>
              <div className="mt-8 space-y-3">
                {/* <a
                  href={`tel:${PHONE.replace(/\s+/g, "")}`}
                  className="flex items-center justify-center gap-2 rounded-full border border-navy-900/10 px-5 py-3 font-semibold text-navy-900"
                >
                  <Phone className="h-4 w-4 text-emerald2-500" /> {PHONE}
                </a> */}
                <Link
                  to={user ? (isAdmin ? "/admin" : "/account") : "/login"}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-full border border-navy-900/15 px-5 py-3 font-semibold text-navy-900"
                >
                  {isAdmin ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  {user ? (isAdmin ? "Admin Dashboard" : "My Account") : "Log In"}
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-full bg-gold-500 px-5 py-3 font-semibold text-navy-950"
                >
                  Book Free Consultation <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
